import rp, { createScheduleControls, encodeSchedule } from '@reporting/rp-app-host';
import { Form, FormAdapter, FormAlert, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import { msg } from "@omegagrid/localize";
import { TreeSource } from "@omegagrid/tree";
import { ReportEmail, Embedding, ModuleCode } from '@reporting/rp-types/accounting';
import { Thread } from '@reporting/rp-types/etl';

export class ReportEmailFormAdapter extends FormAdapter {

	private frequencyType: number;

	constructor(
		private a: number,
		private email: ReportEmail = null,
	) {
		super();
		this.frequencyType = this.email?.freq_type || 1;
	}

	private formFields = ['recipient', 'copy_recipient', 'blind_copy_recipient', 'custom_parameters', 'subject', 'body'] as const;

	getColumns() {
		return [
			{ width: 150 },
			{ width: null },
			{ width: 85 },
		];
	}

	async getControls(): Promise<FormControlProperties[]> {
		const reportValue = this.email?.id_embedding ? `E:${this.email.id_embedding}` : this.email?.id_thread ? `T:${this.email.id_thread}` : null;
		let r = 0;
		const controls: FormControlProperties[] = [
			{row: r, col: 0, type: FormControlType.Label, value: msg('Recipients'), for: 'recipient'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'recipient', value: this.email?.recipient || null, required: true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Copy recipients'), for: 'copy_recipient'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'copy_recipient', value: this.email?.copy_recipient || null},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Blind copy recipients'), for: 'blind_copy_recipient'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'blind_copy_recipient', value: this.email?.blind_copy_recipient || null},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Report'), for: '_report'},
			{row: r++, col: 1, cs: 2, type: FormControlType.List, key: '_report', value: reportValue, content: {
				sourceData: await this.getReportTree(),
				options: { selectMode: 'single', autoClose: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Parameters'), for: 'custom_parameters'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'custom_parameters', value: this.email?.custom_parameters || null},
			{row: r, col: 0, type: FormControlType.Label, value: msg('File name'), for: '_fileName'},
			{row: r, col: 1, cs: 1, type: FormControlType.TextInput, key: '_fileName', value: this.email?.filename?.replace(/\.(xlsx|pdf)$/, '') || null},
			{row: r++, col: 2, cs: 1, type: FormControlType.List, value: this.email?.filename?.endsWith('.pdf') ? 'pdf' : 'xlsx', key: '_extension', content: {
				sourceData: [
					{ k: 'xlsx', v: '.xlsx' },
					{ k: 'pdf', v: '.pdf' },
				], options: { selectMode: 'single', autoClose: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Subject'), for: 'subject'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'subject', value: this.email?.subject || null},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Text'), for: 'body'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'body', value: this.email?.body || null},
		];

		const schedule = createScheduleControls(this.email, this.frequencyType, r);
		controls.push(...schedule.controls);
		return controls;
	}

	async getReportTree(): Promise<TreeSource> {
		const tree: TreeSource = [];
		const embeddings = await rp.ms('accounting').get<Embedding[]>('embeddings', { accounting: this.a });
		let modules: string[] = [];
		embeddings.forEach(e => {
			if (!modules.includes(e.module)) {
				modules.push(e.module);
				tree.push({ k: '', v: this.translateModuleCode(e.module as ModuleCode), l: 0, ch: false, collapsed: true});
			}
			tree.push({ k: `E:${e.id_embedding}`, v: e.name, l: 1 });
		});
		const threads = await rp.ms('etl').get<Thread[]>('threads', { accounting: this.a });
		if (threads.length > 0) {
			tree.push({ k: '', v: 'Export', l: 0, ch: false, collapsed: true });
			threads.forEach(t => {
				tree.push({ k: `T:${t.id_thread}`, v: t.description, l: 1 });
			});
		}
		return tree;
	}

	translateModuleCode(code: ModuleCode): string {
		const a = rp.accountingProvider.accounting;
		switch (code) {
			case 'F':
				return a.fin_module_name || msg('Finance');
			case 'I':
				return a.item_module_name || msg('Items');
			case 'A':
				return a.balance_module_name || msg('Aging');
			case 'C':
				return 'Custom';
			default:
				return code;
		}
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const report = formValues._report as string || '';
		const newEmail: ReportEmail = {
			...Object.fromEntries(this.formFields.map(key => [key, formValues[key]])),
			idz: this.email?.idz || null,
			id_embedding: report.startsWith('E:') ? report.slice(2) : null,
			id_thread: report.startsWith('T:') ? parseInt(report.slice(2)) : null,
		};

		if (formValues._fileName) {
			newEmail.filename = `${formValues._fileName}.${formValues._extension}`;
		}
		Object.assign(newEmail, encodeSchedule(formValues));

		await rp.ms('accounting').post('auto/emails', newEmail, { accounting: this.a });
		return true;
	}

	updateValues = (controls: FormControlProperties[], values: FormValues) => {
		if (!values) return controls;
		const map = new Map(controls.filter(item => item.key).map(item => [item.key, item]));
		Object.entries(values).forEach(([k, v]) => Object.assign(map.get(k) || {}, { value: v }));
		return controls;
	}

	async updateFormControls(form: Form) {
		const freqType = form.values.freq_type as number;
		if (freqType !== this.frequencyType) {
			this.frequencyType = freqType;
			form.setSourceData({
				controls: this.updateValues(await this.getControls(), form.values),
				columns: this.getColumns()
			});
		}
	}

};
